import { Checkbox, Currency, Table } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useDateTimeFormat } from '~/hooks';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { colors, weights } from '~/styles';

const StyledCheckbox = styled.div`
  > label > div {
    background: ${colors.white};
    width: 1.125rem;
    height: 1.125rem;
    font-size: 0.5rem;
  }
`;

const StyledTable = styled(Table)`
  font-size: 0.875rem;
  margin-top: 2rem;

  ${Table.Cell} {
    align-items: flex-start;
    height: 100%;
    min-height: unset;
    padding: 1rem;
  }
`;

const TotalsRow = styled(Table.Row)`
  font-weight: ${weights.bold};
  border: none;
`;

const TotalLabel = styled.p`
  flex: 1;
  text-align: right;
`;

function TransactionsDrawerPrepaymentsTab({ invoice, prepayments, start, end, projectId, currency, onChange }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const q = {
        start: start || undefined,
        end: end || undefined,
        projectId: projectId || undefined,
      };
      const { data } = await api.www.workspaces(workspace.id).invoices(invoice.id).getPrepaymentTransactions(q);
      setData(data);
    } catch {
      setData(null);
    }
  }, [workspace.id, start, end, projectId, api, invoice.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selected = useMemo(() => {
    if (!data) return;
    return {
      prepayments: _.filter(data, (p) => prepayments.some((e) => e.id === p.id)),
      get amount() {
        return _.sumBy(this.prepayments, (p) => p.fee);
      },
      get some() {
        return this.prepayments.length > 0;
      },
      get all() {
        return this.prepayments.length === data.length;
      },
    };
  }, [data, prepayments]);

  const dateTimeFormat = useDateTimeFormat();

  if (!data) return null;

  const handleBulkSelectionChange = () => {
    onChange(
      selected.some
        ? prepayments.filter(({ id }) => !data.some((p) => id === p.id))
        : [...prepayments, ...data.map(({ id }) => ({ id }))],
    );
  };

  return (
    <div>
      <StyledTable>
        <Table.Header>
          <Table.Column align="center" width="3rem">
            <StyledCheckbox>
              <Checkbox checked={selected.some} partial={!selected.all} onChange={handleBulkSelectionChange} />
            </StyledCheckbox>
          </Table.Column>
          <Table.Column>Details</Table.Column>
          <Table.Column width="6rem" align="right">
            Amount
          </Table.Column>
        </Table.Header>
        <Table.Body>
          {data.map((p) => {
            const checked = _(prepayments).some(({ id }) => id === p.id);
            const handleSelectionChange = () => {
              onChange(checked ? prepayments.filter(({ id }) => id !== p.id) : [...prepayments, { id: p.id }]);
            };
            return (
              <Table.Row key={p.id}>
                <Table.Cell>
                  <StyledCheckbox>
                    <Checkbox checked={checked} onChange={handleSelectionChange} />
                  </StyledCheckbox>
                </Table.Cell>
                <Table.Cell>{_.compact([dateTimeFormat.format(p.date), p.description]).join(' - ')}</Table.Cell>
                <Table.Cell>
                  <strong>
                    <Currency value={p.fee} currency={currency} />
                  </strong>
                </Table.Cell>
              </Table.Row>
            );
          })}
          <TotalsRow>
            <Table.Cell />
            <Table.Cell>
              <TotalLabel>Total</TotalLabel>
            </Table.Cell>
            <Table.Cell>
              <Currency value={selected.amount} currency={currency} />
            </Table.Cell>
          </TotalsRow>
        </Table.Body>
      </StyledTable>
    </div>
  );
}

export default TransactionsDrawerPrepaymentsTab;
